import {
  createFileRoute,
  Link,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";

import {
  signInWithGithub,
  signInWithGoogle,
  signUpWithEmail,
} from "@/auth/auth-client";
import { StructuredData } from "@/shared/components/structured-data";
import {
  seo,
  createPageTitle,
  getCanonicalLinks,
} from "@/utils/seo";
// oxlint-disable-next-line import/no-unassigned-import -- CSS side-effect import is the canonical pattern for scoped styles
import "@/speech-agent/speech-agent.css";

const SIGN_UP_TITLE_PARTS = ["Registrace"];

function SignUpPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.body.classList.add("in-speech-agent");
    return () => {
      document.body.classList.remove("in-speech-agent");
    };
  }, []);

  const callbackURL = "/app/speech-agent";

  const handleSubmit = async (
    e: React.SyntheticEvent<HTMLFormElement>
  ) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Heslo musí mít alespoň 8 znaků.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await signUpWithEmail(
        email,
        password,
        name,
        callbackURL
      );
      if (result.error) {
        setError(
          result.error.message ??
            "Při registraci nastala chyba."
        );
      }
    } catch {
      setError("Při registraci nastala chyba.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="canvas" aria-hidden="true" />
      <div className="grain" aria-hidden="true" />
      <div className="app-shell">
        <StructuredData
          url="https://speech-agent.cz/sign-up"
          description="Vytvoř si účet ve Speech Agent a začni si vyprávět vlastní zápisy."
          about="User Registration"
          titleParts={SIGN_UP_TITLE_PARTS}
        />
        <header className="site-header">
          <Link to="/sign-up" className="brand">
            <span className="brand-mark" aria-hidden="true">
              <span className="brand-mark-dot" />
            </span>
            <span className="brand-name">
              Speech
              <span className="brand-name-thin">
                {" "}
                Agent
              </span>
            </span>
          </Link>
        </header>
        <main>
          <section className="page" id="page-register">
            <div className="auth-shell">
              <div className="auth-side">
                <p className="kicker">— 01 / Nový účet</p>
                <h1 className="display">
                  Začni si <em>vyprávět</em>
                  <br />
                  své vlastní zápisy.
                </h1>
                <p className="lede">
                  Zaregistruj se a dostaneš výchozího agenta
                  pro zápis z porady. Vlastní si přidáš
                  později — stačí napsat instrukce v
                  Markdownu.
                </p>
              </div>

              <div className="card auth-card">
                <p className="kicker">— Registrace</p>
                <h2 className="card-title">
                  Vytvořit účet
                </h2>
                <form
                  onSubmit={handleSubmit}
                  autoComplete="on"
                >
                  <label
                    className="field-label"
                    htmlFor="reg-name"
                  >
                    Jméno
                  </label>
                  <input
                    id="reg-name"
                    type="text"
                    required
                    autoComplete="name"
                    placeholder="Jan Novák"
                    value={name}
                    onChange={(e) =>
                      setName(e.target.value)
                    }
                  />
                  <label
                    className="field-label"
                    htmlFor="reg-email"
                  >
                    E-mail
                  </label>
                  <input
                    id="reg-email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="jmeno@firma.cz"
                    value={email}
                    onChange={(e) =>
                      setEmail(e.target.value)
                    }
                  />
                  <label
                    className="field-label"
                    htmlFor="reg-password"
                  >
                    Heslo
                  </label>
                  <input
                    id="reg-password"
                    type="password"
                    required
                    minLength={8}
                    autoComplete="new-password"
                    placeholder="alespoň 8 znaků"
                    value={password}
                    onChange={(e) =>
                      setPassword(e.target.value)
                    }
                  />
                  <p className="form-hint">
                    Minimálně 8 znaků.
                  </p>
                  <button
                    type="submit"
                    className="primary-btn"
                    disabled={submitting}
                  >
                    {submitting
                      ? "Vytvářím účet…"
                      : "Založit účet"}
                    <span className="arrow">→</span>
                  </button>
                  {error && (
                    <p className="status error">{error}</p>
                  )}
                </form>
                <p className="auth-switch">
                  Už máš účet?{" "}
                  <Link to="/sign-in">Přihlas se</Link>
                </p>
                <details
                  style={{
                    marginTop: "1rem",
                    color: "var(--fg-muted)",
                    fontSize: "0.85rem",
                  }}
                >
                  <summary>Jiné způsoby</summary>
                  <div
                    style={{
                      marginTop: "0.5rem",
                      display: "flex",
                      gap: "0.5rem",
                      flexDirection: "column",
                    }}
                  >
                    <button
                      type="button"
                      className="ghost-btn"
                      onClick={() =>
                        void signInWithGithub(callbackURL)
                      }
                    >
                      Registrovat přes Github
                    </button>
                    <button
                      type="button"
                      className="ghost-btn"
                      onClick={() =>
                        void signInWithGoogle(callbackURL)
                      }
                    >
                      Registrovat přes Google
                    </button>
                  </div>
                </details>
              </div>
            </div>
          </section>
        </main>
        <footer className="site-footer">
          <span className="footer-mark">⌖</span>
          <span className="footer-text">
            Speech Agent · PoC
          </span>
        </footer>
      </div>
    </>
  );
}

export const Route = createFileRoute("/sign-up")({
  component: SignUpPage,
  head: () => ({
    meta: [
      ...seo({
        title: createPageTitle("Registrace"),
        description:
          "Vytvoř si účet ve Speech Agent a začni si vyprávět vlastní zápisy.",
        keywords:
          "speech agent, registrace, hlasový agent, vytvořit účet",
        canonical: "https://speech-agent.cz/sign-up",
      }),
    ],
    links: [
      ...getCanonicalLinks(
        "https://speech-agent.cz/sign-up"
      ),
    ],
  }),
});
